import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';
import Sidebar from '../../components/ui/Sidebar';
import { Button } from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { supabase } from '../../lib/supabase';
import * as partnerService from '../../services/partnerService';
import {
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Eye,
  AlertCircle
} from 'lucide-react';

interface Offer {
  id: string;
  title: string;
  category: string;
  status: 'active' | 'inactive' | 'expired';
  discount_type: string;
  discount_percentage: number | null;
  discount_amount: number | null;
  created_at: string;
  business_name: string;
}

const PartnerOfferManagement: React.FC = () => {
  const navigate = useNavigate();
  const { partner, loading: authLoading } = usePartnerAuth();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // NEW: Offer capacity tracking
  const [offerCapacity, setOfferCapacity] = useState({
    currentCount: 0,
    maxAllowed: 5,
    remaining: 5,
    canCreate: true
  });

  useEffect(() => {
    if (!authLoading && partner) {
      loadOffers();
      loadOfferCapacity(); // NEW: Load offer capacity
    }
  }, [partner, authLoading]);

  // NEW: Load partner offer capacity
  const loadOfferCapacity = async () => {
    if (!partner) return;
    
    try {
      const { data, error } = await supabase.rpc('can_partner_create_offer', {
        p_partner_id: partner.id
      });

      if (error) throw error;

      if (data && data.length > 0) {
        const capacity = data[0];
        setOfferCapacity({
          currentCount: capacity.current_count,
          maxAllowed: capacity.max_allowed,
          remaining: capacity.remaining,
          canCreate: capacity.can_create
        });
      }
    } catch (error) {
      console.error('Error loading offer capacity:', error);
    }
  };

  const loadOffers = async () => {
    if (!partner) return;

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('offers')
        .select('*')
        .eq('partner_id', partner.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setOffers(data || []);
    } catch (error) {
      console.error('Error loading offers:', error);
      alert('Failed to load offers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOffer = () => {
    // Check if partner can create more offers
    if (!offerCapacity.canCreate) {
      alert(`You have reached your maximum offer limit (${offerCapacity.currentCount}/${offerCapacity.maxAllowed} offers used). Please contact admin to increase your limit.`);
      return;
    }
    navigate('/partner-create-offer');
  };

  const handleEditOffer = (offerId: string) => {
    navigate(`/partner-create-offer?edit=${offerId}`);
  };

  const handleDeleteOffer = async (offerId: string, offerTitle: string) => {
    if (!window.confirm(`Are you sure you want to delete "${offerTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await partnerService.deleteOffer(offerId);
      alert('Offer deleted successfully');
      loadOffers(); // Reload offers
      loadOfferCapacity(); // Reload capacity after deletion
    } catch (error) {
      console.error('Error deleting offer:', error);
      alert('Failed to delete offer. Please try again.');
    }
  };

  const handleToggleOfferStatus = async (offerId: string, currentStatus: string, offerTitle: string) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? 'activate' : 'deactivate';
    
    if (!window.confirm(`Are you sure you want to ${action} "${offerTitle}"?`)) {
      return;
    }

    try {
      if (newStatus === 'active') {
        await partnerService.activateOffer(offerId);
      } else {
        await partnerService.deactivateOffer(offerId);
      }
      
      alert(`Offer ${action}d successfully`);
      loadOffers(); // Reload offers
    } catch (error) {
      console.error(`Error ${action}ing offer:`, error);
      alert(`Failed to ${action} offer. Please try again.`);
    }
  };

  const handleViewOffer = (offerId: string) => {
    navigate(`/partner-offer-details/${offerId}`);
  };

  // Filter offers based on search and status
  const filteredOffers = offers.filter(offer => {
    const matchesSearch = offer.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         offer.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || offer.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (authLoading || loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {authLoading ? 'Checking authentication...' : 'Loading offers...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar 
        isCollapsed={sidebarCollapsed}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        userRole="partner"
      />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm border-b border-gray-200 h-16 flex items-center justify-between px-6 shrink-0">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Offer Management</h1>
            <p className="text-xs text-gray-600">Manage your offers and track performance</p>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          {/* NEW: Offer Capacity Banner */}
          <div className={`mb-6 p-4 rounded-lg flex items-center justify-between ${
            offerCapacity.remaining === 0 ? 'bg-red-50 border border-red-200' : 
            offerCapacity.remaining <= 2 ? 'bg-yellow-50 border border-yellow-200': 'bg-blue-50 border border-blue-200'
          }`}>
            <div className="flex items-center gap-3">
              <AlertCircle className={`w-6 h-6 ${
                offerCapacity.remaining === 0 ? 'text-red-600' : 
                offerCapacity.remaining <= 2 ? 'text-yellow-600': 'text-blue-600'
              }`} />
              <div>
                <p className="font-semibold text-gray-900">
                  Offer Capacity: {offerCapacity.currentCount}/{offerCapacity.maxAllowed} offers
                </p>
                <p className="text-sm text-gray-600">
                  {offerCapacity.remaining === 0 
                    ? 'You have reached your maximum offer limit. Contact admin to increase.'
                    : `You can create ${offerCapacity.remaining} more offer${offerCapacity.remaining === 1 ? '' : 's'}.`
                  }
                </p>
              </div>
            </div>
            <Button
              onClick={handleCreateOffer}
              disabled={!offerCapacity.canCreate}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Offer
              {!offerCapacity.canCreate && <span className="ml-1 text-xs">(Limit Reached)</span>}
            </Button>
          </div>

          {/* Search and Filter Bar */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search offers by title or category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="text-gray-400 w-5 h-5" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>
          </div>

          {/* Offers Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Your Offers</h2>
              <p className="text-sm text-gray-600">
                Showing {filteredOffers.length} of {offers.length} offers
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Offer Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Discount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOffers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                        <div className="flex flex-col items-center">
                          <p className="text-lg font-medium mb-2">No offers found</p>
                          <p className="text-sm mb-4">
                            {searchTerm || filterStatus !== 'all' ?'Try adjusting your search or filters' :'Create your first offer to start attracting customers'
                            }
                          </p>
                          {!searchTerm && filterStatus === 'all' && (
                            <Button 
                              onClick={handleCreateOffer} 
                              className="flex items-center gap-2"
                              disabled={!offerCapacity.canCreate}
                            >
                              <Plus className="w-4 h-4" />
                              Create Offer
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredOffers.map((offer) => (
                      <tr key={offer.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{offer.title}</div>
                          <div className="text-xs text-gray-500">{offer.business_name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{offer.category}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">
                            {offer.discount_type === 'percentage' 
                              ? `${offer.discount_percentage}%` 
                              : `€${offer.discount_amount}`
                            }
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                            ${offer.status === 'active' ? 'bg-green-100 text-green-800' : 
                              offer.status === 'inactive' ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'}
                          `}>
                            {offer.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(offer.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewOffer(offer.id)}
                              className="text-gray-600 hover:text-gray-900 p-1 rounded hover:bg-gray-100"
                              title="View Details"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleEditOffer(offer.id)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                              title="Edit Offer"
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleToggleOfferStatus(
                                offer.id, 
                                offer.status, 
                                offer.title
                              )}
                              className={`p-1 rounded hover:bg-opacity-10 ${
                                offer.status === 'active' ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'
                              }`}
                              title={offer.status === 'active' ? 'Deactivate' : 'Activate'}
                            >
                              {offer.status === 'active' ? (
                                <ToggleRight className="w-5 h-5" />
                              ) : (
                                <ToggleLeft className="w-5 h-5" />
                              )}
                            </button>
                            <button
                              onClick={() => handleDeleteOffer(offer.id, offer.title)}
                              className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                              title="Delete Offer"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PartnerOfferManagement;